import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Lock, ExternalLink } from "lucide-react";
import { format, parseISO } from "date-fns";

/**
 * Public read-only view for a share-link token. No auth required — the token
 * itself is the credential. Used by auditors, board members, or medical
 * executives who receive a QAPI snapshot / monthly packet / daily digest link.
 */
export default function SharedView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/shared/${token}`;
    axios.get(url)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Unable to load this share link."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center text-[#475569]">
        Loading shared QAPI report…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center justify-center gap-2 text-[#475569] p-6" data-testid="shared-error">
        <Lock className="w-8 h-8 text-[#b94a48]" />
        <div className="text-lg font-semibold text-[#0f172a]">Access denied</div>
        <div className="text-sm">{error}</div>
      </div>
    );
  }

  const snap = data?.snapshot;
  const digest = data?.digest;
  const payload = snap?.payload || null;
  const dateStr = payload?.date || digest?.date || data?.ref;

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="shared-view-root">
      <header className="border-b border-[#e2e8f0] bg-white px-6 py-5">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <svg
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-8 h-8 text-[#0284c7]"
            aria-hidden="true"
          >
            <path d="M16 28c6.627 0 12-5.373 12-12S22.627 4 16 4 4 9.373 4 16s5.373 12 12 12Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 16c0-3.866 3.134-7 7-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="16" cy="16" r="2.5" fill="currentColor"/>
          </svg>
          <div>
            <div className="font-heading font-bold text-lg tracking-tight text-[#0f172a] leading-tight">nikorounds</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#94a3b8]">Shared QAPI Report · Read-only</div>
          </div>
          {snap && (
            <div className="ml-auto text-[10px] text-[#475569] font-mono">
              sha256 {snap.sha256?.slice(0, 12)}…{snap.signed_by && ` · signed by ${snap.signed_by}`}
            </div>
          )}
        </div>
        {(data.label || `${data.resource} · ${data.ref}`) && (
          <div className="max-w-5xl mx-auto mt-3 font-heading text-2xl text-[#0f172a] tracking-tight">
            {data.label || `${data.resource} · ${data.ref}`}
          </div>
        )}
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-6">
        {dateStr && (
          <div className="text-sm text-[#475569]">
            <span className="font-semibold text-[#0f172a]">{format(parseISO(dateStr.length === 10 ? dateStr : dateStr + "-01"), dateStr.length === 10 ? "EEEE, MMM d, yyyy" : "MMMM yyyy")}</span>
            {snap && (
              <span className="ml-3 px-2 py-0.5 rounded bg-[#0284c7] text-white text-[10px] font-semibold uppercase tracking-wide">
                Frozen at {snap.taken_at?.slice(0, 19).replace("T", " ")} UTC
              </span>
            )}
          </div>
        )}

        {(payload || digest) && <SnapshotDetail payload={payload || digest} live={!snap} />}
      </main>

      <footer className="max-w-5xl mx-auto p-6 text-[10px] text-[#475569] text-center">
        This is a read-only view generated from a signed QAPI snapshot. The underlying
        data was frozen at the timestamp above and cannot be altered from this link.
      </footer>
    </div>
  );
}

function SnapshotDetail({ payload, live }) {
  const day = payload.day || {};
  const indicators = payload.indicators || [];
  const checks = payload.checks || [];
  const tasks = payload.tasks || [];
  const caps = payload.caps || [];
  const patientsCount = ((day.patients || []).length) + ((day.unassigned || []).length);
  const openTasks = tasks.filter((t) => t.status !== "completed");
  const activeCaps = caps.filter((c) => c.status !== "completed");

  // Per-indicator compliance
  const checkedByInd = {};
  for (const ck of checks) {
    if (!ck.checked) continue;
    (checkedByInd[ck.indicator_id] = checkedByInd[ck.indicator_id] || new Set()).add(ck.patient_key);
  }
  const total = patientsCount || 1;

  return (
    <div className="space-y-5">
      {live && (
        <div className="p-3 rounded bg-[#fcf3e1] border border-[#e0d0a4] text-sm text-[#7a5a1a]" data-testid="shared-live-warn">
          Note: this day has not been frozen. The values below reflect the current live state.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric label="Patients" value={patientsCount} />
        <Metric label="Indicators" value={indicators.length} />
        <Metric label="Open tasks" value={openTasks.length} />
        <Metric label="Active CAPs" value={activeCaps.length} />
      </div>

      <div>
        <h3 className="font-heading text-lg text-[#0f172a] mb-2">Compliance today</h3>
        <div className="rounded-md border border-[#e2e8f0] overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f0f9ff] text-[#0284c7]">
                <th className="text-left px-3 py-2 font-semibold">Indicator</th>
                <th className="text-right px-3 py-2 font-semibold w-24">Target</th>
                <th className="text-right px-3 py-2 font-semibold w-24">Actual</th>
                <th className="text-center px-3 py-2 font-semibold w-24">Status</th>
              </tr>
            </thead>
            <tbody>
              {indicators.map((ind) => {
                const done = (checkedByInd[ind.id] || new Set()).size;
                const pct = Math.round((100 * done) / total * 10) / 10;
                const onTarget = ind.target_pct === 0 || pct >= ind.target_pct;
                return (
                  <tr key={ind.id} className={onTarget ? "" : "bg-[#fbebe7]"}>
                    <td className="px-3 py-1.5">{ind.label}</td>
                    <td className="text-right px-3 py-1.5">{ind.target_pct ? `${ind.target_pct}%` : "—"}</td>
                    <td className="text-right px-3 py-1.5 font-semibold">{pct}%</td>
                    <td className="text-center px-3 py-1.5 text-xs">{onTarget ? "On target" : "Below"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {openTasks.length > 0 && (
        <div>
          <h3 className="font-heading text-lg text-[#0f172a] mb-2">Open tasks ({openTasks.length})</h3>
          <ul className="space-y-1.5">
            {openTasks.slice(0, 30).map((t) => (
              <li key={t.id} className="p-2 rounded border border-[#e2e8f0] bg-white text-sm">
                <span className="font-semibold">{t.title}</span>
                {t.due_date && <span className="ml-2 text-xs text-[#b94a48]">due {t.due_date}</span>}
                {t.patient_name && <span className="ml-2 text-xs text-[#475569]">· {t.patient_name}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {activeCaps.length > 0 && (
        <div>
          <h3 className="font-heading text-lg text-[#0f172a] mb-2">Corrective Action Plans</h3>
          <ul className="space-y-2">
            {activeCaps.map((c) => (
              <li key={c.id} className="p-3 rounded border border-[#e2e8f0] bg-white text-sm space-y-1">
                <div className="font-semibold">
                  {c.month} · {indicators.find((i) => i.id === c.indicator_id)?.label || "(indicator)"}
                </div>
                {c.root_cause && <div className="text-xs"><span className="font-semibold">Root cause:</span> {c.root_cause}</div>}
                {c.action_steps && <div className="text-xs"><span className="font-semibold">Action steps:</span> {c.action_steps}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="p-3 rounded border border-[#e2e8f0] bg-white">
      <div className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">{label}</div>
      <div className="font-heading text-3xl text-[#0f172a] mt-0.5">{value}</div>
    </div>
  );
}
